import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api, money } from "@/lib/api";
import { MapPin, TrendingUp, CheckCircle2, Building2 } from "lucide-react";

const pct = (v) => (v == null ? "—" : `${v}%`);

export default function InvestorLocations() {
  const { session, data, next, back, mergeData } = useJourney();
  const [regions, setRegions] = useState([]);
  const [activeId, setActiveId] = useState(data.investor_location?.region_id || null);
  const [suburb, setSuburb] = useState(data.investor_location?.suburb || null);

  useEffect(() => {
    api.investorRegions().then((d) => {
      setRegions(d.regions || []);
      if (!activeId && d.regions?.length) setActiveId(d.regions[0].id);
    }).catch(() => toast.error("Could not load Perth regions."));
  }, []); // eslint-disable-line

  const active = regions.find((r) => r.id === activeId);

  const saveNext = () => {
    if (!active) { toast.error("Choose a corridor to continue."); return; }
    const sub = active.suburbs.find((s) => s.name === suburb) || active.suburbs[0];
    const investor_location = {
      region_id: active.id, region_name: active.name, corridor: active.corridor,
      suburb: sub?.name || null, median: sub?.median ?? null, rent_pw: sub?.rent_pw ?? null,
      gross_yield: sub?.gross_yield ?? null, price_growth_12m: sub?.price_growth_12m ?? null,
    };
    mergeData({ investor_location });
    if (session) api.patchSession(session.id, { data: { investor_location } }).catch(() => {});
    next();
  };

  return (
    <StageFrame testId="stage-investor-locations" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Where the numbers look strongest</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
          Perth's best new greenfield corridors.
        </h1>
        <p className="text-mutedText mt-3 max-w-2xl">Pick a corridor, then the suburb whose investment numbers suit your plan. Data from the Propintellix Perth WA profile.</p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-8">
          <div className="lg:col-span-4 space-y-2" data-testid="investor-region-list">
            {regions.map((r) => (
              <button key={r.id} data-testid={`region-${r.id}`} onClick={() => { setActiveId(r.id); setSuburb(null); }}
                className={`w-full text-left rounded-2xl border p-4 transition ${activeId === r.id ? "border-brand bg-brand/10" : "border-line bg-white/[0.03] hover:border-brand/40"}`}>
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow text-brand">{r.corridor}</span>
                  <span className="text-[10px] uppercase tracking-widest text-mutedText">{r.invest_view}</span>
                </div>
                <div className="text-forest font-medium mt-1 text-sm leading-snug">{r.name}</div>
                <div className="text-xs text-mutedText mt-1">{r.tagline}</div>
              </button>
            ))}
          </div>

          {active && (
            <motion.div key={active.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
              className="lg:col-span-8 rounded-2xl border border-line bg-white/[0.03] p-6" data-testid="investor-region-detail">
              <div className="flex items-center gap-2 text-brand"><TrendingUp size={18} /><span className="label-eyebrow text-forest/70">Why this corridor</span></div>
              <p className="text-forest/90 mt-2 text-sm leading-relaxed">{active.growth_story}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {active.estates.map((e, i) => (
                  <span key={i} title={e.note} className="rounded-full border border-line bg-surface px-3 py-1 text-xs text-forest/80">
                    {e.name}{e.developer ? ` · ${e.developer}` : ""}
                  </span>
                ))}
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm" data-testid="investor-suburb-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-mutedText border-b border-line">
                      <th className="py-2 pr-3">Suburb</th><th className="py-2 pr-3">Median</th><th className="py-2 pr-3">Rent</th>
                      <th className="py-2 pr-3">Yield</th><th className="py-2 pr-3">12m growth</th><th className="py-2">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.suburbs.map((s) => {
                      const sel = suburb === s.name;
                      return (
                        <tr key={s.name} data-testid={`suburb-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                          onClick={() => setSuburb(s.name)}
                          className={`cursor-pointer border-b border-line/60 transition ${sel ? "bg-brand/10" : "hover:bg-white/[0.04]"}`}>
                          <td className="py-2.5 pr-3 text-forest font-medium flex items-center gap-2">
                            {sel && <CheckCircle2 size={14} className="text-brand" />}{s.name}
                          </td>
                          <td className="py-2.5 pr-3 text-forest/80">{s.median ? money(s.median) : "—"}</td>
                          <td className="py-2.5 pr-3 text-forest/80">{s.rent_pw ? `$${s.rent_pw}/wk` : "—"}</td>
                          <td className="py-2.5 pr-3 text-emerald-300">{pct(s.gross_yield)}</td>
                          <td className="py-2.5 pr-3 text-brand">{pct(s.price_growth_12m)}</td>
                          <td className="py-2.5 text-mutedText text-xs">{s.view}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-mutedText/70 mt-3">Yields are suburb-level (REIWA median house price ÷ median rent) — indicative, not estate-specific.</p>
            </motion.div>
          )}
        </div>
      </div>

      <ConsultantNav testId="stage-investor-locations" onBack={back} onNext={saveNext} nextLabel="Choose floor plan" />
    </StageFrame>
  );
}
